import type {Component, PropertyProps} from 'grapesjs';
import type {SelectedValueType} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import type WalkthroughPopup from '@/email-maker/grapesjs-email-editor-plugin/src/vos/walkthroughpopup';
import type {ColorPaletteInfo, ColorPalettePill} from '@Libraries/color-palettes.library';
import type {ExtendedControlIcons} from '@/email-maker/grapesjs-email-editor-plugin/src/vos/extendedcontrolicons';
import type {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import type {InputFieldSize} from '@Components/input-field-v2/input-field.types';

export default interface EmailMakerConstants {
  devices: DeviceSettings;
  walkthrough: WalkthroughPopupDefinitions;
  exportOptions: EmailExportOptionsConstants;
  blocks: BlocksConstants;
  commands: CommandsConstants;
  components: ComponentsConstants;
  buttonPresets: Record<number, number>;
  props: Record<string, string>;
  styles: Record<string, string>;
  mediaType: MediaTypes;
  MAX_CONTENT_WIDTH: string;
  mediaImageModal: MediaImageModalTypes;
  isInitializingEditor: boolean;
  RTE_INIT: boolean;
  AUTOSAVE_STATE_SAVING: string;
  AUTOSAVE_STATE_NOT_SAVED: string;
  AUTOSAVE_STATE_SAVED: string;
  DEVICE_CLASS_PREFIX: string;
  DEVICE_DESKTOP: string;
  DEVICE_MOBILE: string;
  DEVICE_TABLET: string;
  AUTO_WIDTH_CTA_CLASS: string;
  EMAIL_DATA_CLIPBOARD_KEY: string;
  EMAIL_COMPONENT_DROPPABLE_KEY: string;
  HIGHLIGHT_HOVER_CLASS: string;
  LOCAL_STORAGE_POSTER_ID_KEY: string;
  LOCAL_STORAGE_POSTER_PAGE_ID_KEY: string;
  ADD_DESIGN_TO_EMAIL_TEMPLATE: string;
  READ_ONLY_FOOTER_ID: string;
  EMAIL_OVERLAY_ID: string;
  EMAIL_MEDIA_OVERLAY_ID: string;
  EMAIL_OVERLAY_ANIMATION_TIME_MS: number;
  COMPONENT_LOADING_OVERLAY_CLASS: string;
  COMPONENT_LOADING_OVERLAY_WRAPPER_ID: string;
  MORE_OPTIONS_DIALOG: string;
  EXPORT_OPTIONS_DIALOG: string;
  EXPORT_OPTIONS_DIALOG_MOBILE: string;
  MAX_HTML_CONTENT_LENGTH: number;
  MAX_VIDEOS_ALLOWED: number;
  MAX_TRIES_TO_FIND_TOOLBAR: number;
  MOBILE_CSS_PROP_PREFIX: string;
  MODE_START: string;
  MODE_LOAD: string;
  MODE_COPY: string;
  NEW_TEMPLATE_NAME: string;
  TABLET_CSS_PROP_PREFIX: string;
  ZOOM_STEP: number;
  ZOOM_VALUE: number;
  DEFAULT_ZOOM: number;
  ZOOM_UPPER_LIMIT: number;
  ZOOM_LOWER_LIMIT: number;
  IS_SCHEDULED: boolean;
  MINUTES_STEP: number;
  RTE_TAGNAMES: string[];
  PUBLISH_CTA: JQuery;
  SCHEDULED_TIME: string;
  IS_SAVING: boolean;
  HAS_SAVED: boolean;
  SAVE_QUEUED: boolean;
  SAVE_BUTTON_TITLE: string;
  SHOULD_SHOW_SAVE_CONFLICT: boolean;
  COPIED_STYLES_COMPONENT: Component | null;
  NONCOPYABLE_STYLES: string[];
  CUSTOM_EVENT_STORAGE_END: string;
  CUSTOM_EVENT_COMPONENT_STYLE_COPIED: string;
  NUM_REMOVE_BACKGROUND_CREDITS: number;
  SIGNUP_FORMS_DATA: UserSignupFormsData;
  COLOR_PALETTES: ColorPaletteInfo[] | undefined;
  COLOR_PALETTE_PILLS: ColorPalettePill[] | undefined;
  DESIGN_COPY: string;
  DESIGN_EDIT: string;
  BUTTON_DEFAULT_PRESET_ID: number;
  WIDTH_DEFAULT_TOGGLE: number;
  WALKTHROUGH_CONTAINER_CLASSNAME: string;
  TOGGLE_OPTION_PRESET_ID_ONE: number;
  TOGGLE_OPTION_PRESET_ID_ZERO: number;
  IS_WALKTHROUGH_POPUP_OPEN: boolean;
  DIVIDER_DEFAULT_HEIGHT: string;
  AUTO_UPDATE_EMAIL_CONTENT_PARAMS?: AutoUpdateEmailContentParams;
  PUPPETEER_CONFIG: EmailMakerPuppeteerConfig | null;
  AI_EDIT_ENABLED: boolean;
  IS_IN_VERSION_HISTORY_MODE: boolean;
}

export enum SaveActionType {
  ACCEPT_YOURS = 'ACCEPT_YOURS',
  ACCEPT_THEIRS = 'ACCEPT_THEIRS',
}

export interface MediaTypes {
  IMAGE: 'image';
  VIDEO: 'video';
  ANIMATED_STICKER: 'animated_sticker';
  STILL_STICKER: 'still_sticker';
  ICON: 'icon';
}

export interface MediaImageModalTypes {
  MY_UPLOADS: 'my-uploads-images';
  EXPLORE_MEDIA: 'explore-media-images';
  AI_IMAGES: 'ai-images';
}

export interface WalkthroughPopupDefinitions {
  MAIN_WALKTHROUGH: WalkthroughPopup;
  INTRODUCTION_MAIN_WALKTHROUGH: WalkthroughPopup;
  HIERARCHY_LEVEL_ONE: WalkthroughPopup;
  HIERARCHY_LEVEL_TWO: WalkthroughPopup;
  ADD_SECTION: WalkthroughPopup;
  ADD_TABLE: WalkthroughPopup;
  ADD_BUTTON: WalkthroughPopup;
  ADD_LINK: WalkthroughPopup;
  ADD_SOCIAL_LINKS: WalkthroughPopup;
  ADD_DESIGN: WalkthroughPopup;
}

export interface EmailExportOptionsConstants {
  COPY_EMAIL: number;
  COPY_HTML: number;
  DOWNLOAD_HTML: number;
}

export interface BlocksConstants {
  CATEGORY_SECTIONS: string;
  CATEGORY_ELEMENTS: string;
  CATEGORY_TEXT: string;
  CATEGORY_IMAGE: string;
  CATEGORY_MY_DESIGNS: string;
  BLOCK_AI_EDIT: string;
  ID_BLOCKS_MANAGER_CONTAINER: string;
  ID_SECTION_PRESET_1: string;
  ID_SECTION_PRESET_2: string;
  ID_SECTION_PRESET_3: string;
  ID_SECTION_PRESET_4: string;
  ID_SECTION_PRESET_5: string;
  ID_SECTION_PRESET_6: string;
  ID_TEXT_PRESET_1: string;
  ID_TEXT_PRESET_2: string;
  ID_UPLOAD_CONTENT: string;
  ID_EXPLORE_MEDIA: string;
  ID_AI_IMAGES: string;
  ID_DESIGN: string;
  ID_BUTTON: string;
  ID_SOCIAL: string;
  ID_DIVIDER: string;
  ID_TABLE: string;
  ID_STICKER: string;
  ID_ICONS: string;
  ID_VIDEO: string;
  ID_ADD_BLOCKS_DIALOG: string;
}

type CommandsConstants = Record<string, string> & CommandsTypes;

export interface CommandsTypes {
  walkthrough: Record<string, string>;
  text: Record<string, string>;
  mediaModals: Record<string, string>;
}

export type StyleAttributes = Record<string, string>;

export interface BasicComponentCommandProps {
  component?: Component;
}

export interface ComponentsConstants {
  default: Record<string, string>;
  table: Record<string, string>;
  social: Record<string, string>;
  graphic: Record<string, string>;
  container: Record<string, string>;
  text: Record<string, string>;
  misc: Record<string, string>;
}

export interface DeviceSettings {
  DEVICE_CLASS_PREFIX: string;
  DEVICE_DESKTOP: string;
  DEVICE_MOBILE: string;
  DEVICE_TABLET: string;
  AUTO_WIDTH_CTA_CLASS: string;
}

export interface SymbolStorageData {
  content?: string;
  type?: string;
}

export interface EmailMaker {
  devices: DeviceSettings;
  walkthrough: WalkthroughPopupDefinitions;
  exportOptions: EmailExportOptionsConstants;
  blocks: BlocksConstants;
  commands: Record<string, string>;
}

export enum ResponsiveType {
  MOBILE = 'MOBILE',
  TABLET = 'TABLET',
}

export enum ResponsiveAttributeName {
  MOBILE = 'responsive',
  TABLET = 'responsive-tablet',
}

export enum OnLoadUrlParamsConfig {
  AutoUpdateEmailContentParams = 'autoUpdateEmailContentParams',
}

export type ComponentAlignmentStyle = 'middle' | 'right' | 'center' | 'left';

export interface EmailPreviewResponse {
  fileName: string;
  html: string;
}

export interface UserSignupFormsData {
  limit: number;
  offset: number;
  signupForms: UserSignupFormLinkData[];
}

export const mailingListUpdateEvent = 'mailingList::update';

export interface UserSignupFormLinkData {
  name: string;
  url: string;
}

export interface AutoUpdateTextContentParams {
  rewriteContentPrompt: string;
  rewriteContentPromptOptions: SelectedValueType;
}

export type HexColors = string[];

export interface AutoUpdateBrandingParams {
  colors: HexColors;
}

export interface AutoUpdateEmailContentParams {
  autoUpdateEmailTemplateOnLoad: boolean;
  autoUpdateTextContentParams?: AutoUpdateTextContentParams;
  autoUpdateBrandingParams?: AutoUpdateBrandingParams;
}

export interface GA4TrackingEvents {
  SECTIONS_USED: number;
}

export const PROMPT_SESSION_STORAGE_VAR = 'prompt-session-storage-var';
export const GENERATION_OPTIONS_SESSION_STORAGE_VAR = 'generation-options-session-storage-var';
export const TEMPLATE_AI_REGENERATION = 'template-ai-regeneration';
export const TOOLBAR_AI_REWRITE_DROPDOWN_ANCHOR = 'toolbar-ai-rewrite-dropdown-anchor';
export const DISABLE_GJS_TOOLBAR_ACTIVE_STYLE = 'disable-gjs-toolbar-active-style';
export const TEXT_COMPONENT_AI_REWRITE_PROMPT_BOX_INDEX = 3;
export const BUTTON_COMPONENT_AI_REWRITE_PROMPT_BOX_INDEX = 3;

export const EMAIL_MAKER_AI_REWRITE_PROMPT = 'email-maker-ai-rewrite-prompt';
export const EMAIL_MAKER_AI_REWRITE_GENERATION_OPTIONS = 'email-maker-ai-rewrite-generation-options';
export const EMAIL_MAKER_AI_REWRITE_PREVIOUS_PROMPT = 'email-maker-ai-rewrite-previous-prompt';
export const EMAIL_MAKER_AI_REDESIGN_LOADING_TOAST = 'email-maker-ai-redesign-loading-toast';
export const EMAIL_MAKER_AI_REWRITE_LOADING_TOAST = 'email-maker-ai-rewrite-loading-toast';
export const EMAIL_MAKER_AI_REDESIGN_SINGUP_SOURCE = 'email-maker-ai-redesign';
export const EMAIL_MAKER_AI_REWRITE_SINGUP_SOURCE = 'email-maker-ai-rewrite';
export const EMAIL_MAKER_AI_REWRITE_DROPDOWN_SINGUP_SOURCE = 'email-maker-ai-rewrite-dropdown';


export enum ComponentloadingOverlayStylesTarget {
  COMPONENT_DEFINITION = 'COMPONENT_DEFINITION',
  ELEMENT = 'ELEMENT',
}

export enum EmailOverlayStyle {
  LOADING,
  AI_EDIT,
}

export interface ComponentloadingOverlayStyle {
  width: string;
  marginLeft: string;
}

export interface EmailMakerPuppeteerConfig {
  flow: EmailMakerPuppeteerFlow;
}

export enum EmailMakerPuppeteerFlow {
  TEMPLATE_AUTO_BRANDING = 'TEMPLATE_AUTO_BRANDING',
}

export interface CanUseBypassedEmailResponse {
  canUseEmail: boolean;
}

// Slider-specific interfaces
export interface SliderStyleProps extends PropertyProps {
  component?: Component;
  step?: number;
  min?: number;
  max?: number;
  units?: string;
  inputType?: string;
  extendedControls?: boolean;
  property: string;
  propertyLabel?: string; // Make optional to match GrapesJS PropertyProps
  defaults?: string;
  isResponsive?: boolean;
  toggleProp?: string;
  extendedControlIcons?: ExtendedControlIcons;
}

export interface SliderChangeParams {
  el: HTMLElement;
  event: Event;
  complete?: any;
  propertyName?: string;
  isSingleProperty?: boolean;
}

export interface SliderStyleObject {
  topStyle: number;
  bottomStyle: number;
  rightStyle: number;
  leftStyle: number;
}

// Update interfaces to match GrapesJS function signatures
export interface SliderCreateData {
  el: HTMLElement;
  createdEl?: HTMLElement;
  property: any;
  props: SliderStyleProps;
  change: (...args: any) => void;
  updateStyle: (value: string, opts?: any) => void;
}

export interface SliderEmitData {
  el: HTMLElement;
  createdEl?: HTMLElement;
  property: any;
  props: SliderStyleProps;
  change: (...args: any) => void;
  updateStyle: (value: string, opts?: any) => void;
}

export interface SliderUpdateData extends SliderEmitData {
  value: string | number;
}

// Social Links specific interfaces
export interface SocialLinkData {
  id: string;
  name: string;
  baseURL: string;
  logos: Record<string, string>;
}

export interface SocialLinksCreateData {
  props: SocialLinksStyleProps;
  change: (...args: any[]) => void;
}

export interface SocialLinksStyleProps extends PropertyProps {
  component?: Component;
}

export interface SocialLinkInputCustomHandlers {
  onInputFocus: (dataId: string, value: string) => void;
  onInputBlur: (dataId: string, value: string) => void;
}

export interface SocialLinkInputFieldProps {
  inputClassName: string;
  placeholder: string;
  input: string;
  size: InputFieldSize;
}

export interface SocialLinkSuggestionsDropdownProps {
  popupClassName: string;
  popupAlignment: ALIGNMENT_TYPE;
  popupPosition: DROPDOWN_POSITION;
  truncateText: boolean;
  showSuggestionAsTitleOnHover: boolean;
}

export interface SocialLinkDropdownOptions {
  platform: string;
  socialLinkInputCustomHandlers: SocialLinkInputCustomHandlers;
  inputFieldProps: SocialLinkInputFieldProps;
  inputSuggestionsDropdownProps: SocialLinkSuggestionsDropdownProps;
  inputId: string;
  dropdownId: string;
}

export enum UpsertComponentMethod {
  APPEND_AFTER,
  INSERT_INTO,
  REPLACE_WITH,
  PREPEND_BEFORE
}

export interface AsyncGetPreviewSuccessResponse {
  creatorID: string;
  isPremium: boolean;
  isInternal: boolean;
  isReady: string;
  url: string;
}
export enum AsyncGetPreviewErrorCodes {
  POSTER_NOT_PUBLIC = 'POSTER_NOT_PUBLIC',
  POSTER_NOT_FOUND = 'POSTER_NOT_FOUND',
  ERROR = 'ERROR',
}
